import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AccessGrantDto,
  AccessGrantSourceDto,
  AccessScopeDto,
  GrantRole,
  OrganizationRoleType,
  useListAccessGrantsQuery,
  useListDepartmentsQuery,
  useRevokeDepartmentRoleMutation,
  useUpdateOrgUserRoleMutation,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../../common/errors/useApiError";
import { GiveAccessForm } from "./GiveAccessForm";
import { administersDepartmentRole } from "./roleAdministration";
import { asGrantRole, asOrganizationRole, isSameRole } from "./roleVocabulary";
import { useGrantRoleName } from "./useGrantRoleName";

const grantKey = (grant: AccessGrantDto) =>
  `${grant.role}-${grant.scope}-${grant.departmentId ?? grant.eventId ?? "org"}-${grant.source}`;

interface Props {
  personId: string;
  personName: string;
  organizationRoles: OrganizationRoleType[];
  isOpen: boolean;
  onClose: () => void;
}

export const PersonAccessModal = ({
  personId,
  personName,
  organizationRoles,
  isOpen,
  onClose,
}: Props) => {
  const authState = useLoggedInWithOrgContextState();
  const orgId = authState.selectedOrg.id;
  const { t } = useTranslation();
  const toast = useToast();
  const displayer = useApiError();
  const roleName = useGrantRoleName();

  const { data, isLoading } = useListAccessGrantsQuery({
    orgId,
    queryPersonId: personId,
  });
  const { data: departmentData } = useListDepartmentsQuery({ orgId });
  const [revokeDepartmentRole] = useRevokeDepartmentRoleMutation();
  const [updateOrgRoles] = useUpdateOrgUserRoleMutation();
  const [isGivingAccess, setIsGivingAccess] = useState(false);
  const [confirmingRemoval, setConfirmingRemoval] = useState<string | null>(
    null,
  );

  const hasDepartments = (departmentData?.departments.length ?? 0) > 1;
  const grants = useMemo(() => {
    const listed = data?.grants ?? [];
    const covered = new Set(
      listed
        .filter((g) => g.scope === AccessScopeDto.Organization)
        .map((g) => g.role as string),
    );
    const missing: AccessGrantDto[] = organizationRoles
      .filter((role) => !covered.has(role as string))
      .map((role) => ({
        personId,
        firstName: "",
        lastName: "",
        email: "",
        role: asGrantRole(role) as GrantRole,
        scope: AccessScopeDto.Organization,
        departmentId: null,
        departmentName: null,
        eventId: null,
        eventName: null,
        source: AccessGrantSourceDto.Manual,
        issuedAt: "",
      }));
    return [...missing, ...listed];
  }, [data, organizationRoles, personId]);

  const locallyGivenOrganizationRoles = useMemo(
    () => [
      ...new Set(
        grants
          .filter(
            (grant) =>
              grant.scope === AccessScopeDto.Organization &&
              grant.source !== AccessGrantSourceDto.Entra,
          )
          .map((grant) => asOrganizationRole(grant.role))
          .filter((role): role is OrganizationRoleType => role !== null)
      ),
    ],
    [grants]
  );

  const where = (grant: AccessGrantDto) => {
    if (grant.scope === AccessScopeDto.Organization)
      return t("The whole organization");
    if (grant.scope === AccessScopeDto.Department)
      return grant.departmentName || t("Unknown department");
    return grant.eventName || t("Sourcing event without a title");
  };

  const source = (grant: AccessGrantDto) => {
    switch (grant.source) {
      case AccessGrantSourceDto.Entra:
        return t("Managed in Entra");
      case AccessGrantSourceDto.Share:
        return t("Shared on the sourcing event");
      default:
        return t("Given manually");
    }
  };

  const administers = (grant: AccessGrantDto) =>
    grant.scope === AccessScopeDto.Organization
      ? authState.selectedOrg.canManageUsers
      : administersDepartmentRole(authState.selectedOrg, grant.departmentId, grant.role);

  const canRemove = (grant: AccessGrantDto) =>
    grant.source !== AccessGrantSourceDto.Entra &&
    grant.scope !== AccessScopeDto.Event &&
    grant.role !== GrantRole.Owner &&
    administers(grant);

  const remove = async (grant: AccessGrantDto) => {
    setConfirmingRemoval(null);
    const result =
      grant.scope === AccessScopeDto.Department && grant.departmentId
        ? await revokeDepartmentRole({
            orgId,
            departmentId: grant.departmentId,
            departmentRoleRequest: { personId, role: grant.role },
          })
        : await updateOrgRoles({
            orgId,
            userId: personId,
            updateOrgUserRoleRequest: {
              newRoles: locallyGivenOrganizationRoles.filter(
                (r) => !isSameRole(grant.role, r),
              ),
            },
          });

    if ("data" in result) {
      toast({ title: t("Access removed!"), status: "success" });
    } else {
      displayer.trigger(result.error);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {personName}
          <Text fontSize="sm" fontWeight="normal" color="smMuted">
            {t("Access")}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th borderBottomColor="smBorderSubtle">{t("Role")}</Th>
                <Th borderBottomColor="smBorderSubtle">{t("Where")}</Th>
                <Th borderBottomColor="smBorderSubtle">{t("Source")}</Th>
                <Th borderBottomColor="smBorderSubtle" />
              </Tr>
            </Thead>
            <Tbody>
              {grants.map((grant) => (
                <Tr
                  key={grantKey(grant)}
                  _hover={{ backgroundColor: "smBackgroundSecondary" }}
                >
                  <Td borderBottomColor="smBorderSubtle">
                    {grant.role === GrantRole.SourcingViewer &&
                    grant.scope === AccessScopeDto.Event
                      ? t("Can see")
                      : roleName(grant.role)}
                  </Td>
                  <Td borderBottomColor="smBorderSubtle">{where(grant)}</Td>
                  <Td borderBottomColor="smBorderSubtle" color="smMuted">
                    {source(grant)}
                  </Td>
                  <Td
                    borderBottomColor="smBorderSubtle"
                    textAlign="right"
                    whiteSpace="nowrap"
                  >
                    {canRemove(grant) &&
                      (confirmingRemoval === grantKey(grant) ? (
                        <Flex columnGap="1" justifyContent="flex-end">
                          <Button
                            size="xs"
                            colorScheme="red"
                            onClick={() => remove(grant)}
                          >
                            {t("Remove")}
                          </Button>
                          <Button
                            size="xs"
                            variant="ghost"
                            onClick={() => setConfirmingRemoval(null)}
                          >
                            {t("Cancel")}
                          </Button>
                        </Flex>
                      ) : (
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="red"
                          onClick={() => setConfirmingRemoval(grantKey(grant))}
                        >
                          {t("Remove")}
                        </Button>
                      ))}
                  </Td>
                </Tr>
              ))}
              {!grants.length && (
                <Tr>
                  <Td
                    colSpan={4}
                    textAlign="center"
                    color="smMuted"
                    borderBottomColor="smBorderSubtle"
                  >
                    {isLoading
                      ? `${t("Loading")}...`
                      : t("This person has no access")}
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>

          {isGivingAccess ? (
            <GiveAccessForm
              person={{ id: personId, name: personName, organizationRoles: locallyGivenOrganizationRoles }}
              onDone={() => setIsGivingAccess(false)}
            />
          ) : (
            <Button
              mt="4"
              size="sm"
              variant="outline"
              colorScheme="teal"
              onClick={() => setIsGivingAccess(true)}
            >
              {t("Give access")}
            </Button>
          )}

          {hasDepartments && (
            <Text fontSize="xs" color="smMuted" mt="4">
              {t(
                "A department role reaches only what belongs to that department.",
              )}
            </Text>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("Close")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
